import { createSlice } from "@reduxjs/toolkit";
//#region ../src/state/tracking-slice.ts
const initialState = {
	phase: "initializing",
	lastValidPose: null,
	lastSensorOrientation: null,
	lostFrameCount: 0,
	originResetDuringLoss: false,
	resetTransform: void 0,
	lastRestartedPayload: null
};
const trackingSlice = createSlice({
	name: "tracking",
	initialState,
	reducers: {
		/**
		* A valid AR pose was received this frame. Drives INITIALIZING|LOST →
		* TRACKING transitions; on LOST → TRACKING with `originResetDuringLoss`
		* also populates `lastRestartedPayload` (Case 2 of the manager doc).
		*/
		poseReceived(state, action) {
			const { pose, sensorOrientation } = action.payload;
			if (state.phase === "lost") {
				const hadReset = state.originResetDuringLoss;
				const savedResetTransform = state.resetTransform;
				state.originResetDuringLoss = false;
				state.resetTransform = void 0;
				if (hadReset && state.lastValidPose !== null) {
					const lastOrientation = state.lastSensorOrientation ?? sensorOrientation;
					state.lastRestartedPayload = {
						lastValidOdomPos: [
							state.lastValidPose.position.x,
							state.lastValidPose.position.y,
							state.lastValidPose.position.z
						],
						lastValidOdomRot: [
							state.lastValidPose.orientation.x,
							state.lastValidPose.orientation.y,
							state.lastValidPose.orientation.z,
							state.lastValidPose.orientation.w
						],
						lastSensorOrientation: {
							alpha: lastOrientation.alpha,
							beta: lastOrientation.beta,
							gamma: lastOrientation.gamma,
							absolute: lastOrientation.absolute
						},
						newOdomRot: [
							pose.orientation.x,
							pose.orientation.y,
							pose.orientation.z,
							pose.orientation.w
						],
						newSensorOrientation: {
							alpha: sensorOrientation.alpha,
							beta: sensorOrientation.beta,
							gamma: sensorOrientation.gamma,
							absolute: sensorOrientation.absolute
						},
						newOdomPos: [
							pose.position.x,
							pose.position.y,
							pose.position.z
						],
						resetTransform: savedResetTransform
					};
				}
			}
			state.phase = "tracking";
			state.lastValidPose = pose;
			state.lastSensorOrientation = sensorOrientation;
			state.lostFrameCount = 0;
		},
		/**
		* Pose was unavailable this frame. TRACKING → LOST on the first call;
		* subsequent calls only bump `lostFrameCount`.
		*/
		poseLost(state) {
			state.lostFrameCount += 1;
			if (state.phase === "tracking") state.phase = "lost";
		},
		/**
		* `XRReferenceSpace` reset event fired. Only meaningful while LOST.
		* The payload distinguishes three cases:
		*   - `ResetTransformData` — runtime supplied the transform.
		*   - `null` — runtime could not determine the delta.
		*   - `undefined` (action with no payload) — backwards compat for
		*     callers without an event reference.
		*/
		originReset: {
			reducer(state, action) {
				if (state.phase !== "lost") return state;
				return {
					...state,
					originResetDuringLoss: true,
					resetTransform: action.payload
				};
			},
			prepare(transform) {
				return { payload: transform };
			}
		},
		/**
		* Drops the manager back to its initial state. Host calls on new XR
		* session start.
		*/
		resetTracking() {
			return initialState;
		},
		/**
		* Host calls this after firing its `onTrackingRestarted` callback with
		* the contents of `selectLastRestartedPayload`. Failure to call it would
		* leave a stale payload; the test matrix locks this in.
		*/
		clearLastRestartedPayload(state) {
			state.lastRestartedPayload = null;
		}
	}
});
const { poseReceived, poseLost, originReset, resetTracking, clearLastRestartedPayload } = trackingSlice.actions;
const trackingReducer = trackingSlice.reducer;
function selectTrackingPhase(state) {
	return state.tracking.phase;
}
function selectLastValidPose(state) {
	return state.tracking.lastValidPose;
}
function selectLostFrameCount(state) {
	return state.tracking.lostFrameCount;
}
function selectLastRestartedPayload(state) {
	return state.tracking.lastRestartedPayload;
}
/**
* Selector for the last `DeviceOrientation` captured alongside a valid
* AR pose. Used by the §4.3 compass / alignment heading cross-check in
* the tracking-quality reporter (see
* docs/2026-05-16-tracking-quality-metrics-plan.md).
*
* Returns `null` before any pose has been received and after
* `resetTracking` clears the slice.
*/
function selectLastSensorOrientation(state) {
	return state.tracking.lastSensorOrientation;
}
//#endregion
export { clearLastRestartedPayload, originReset, poseLost, poseReceived, resetTracking, selectLastRestartedPayload, selectLastSensorOrientation, selectLastValidPose, selectLostFrameCount, selectTrackingPhase, trackingReducer };
